import type { ModelMessage } from '@/types/msq'

const PTC_CODE_EXECUTION_START_MARKER = '[Code Execution]\n```python\n'
const PTC_CODE_FENCE_CLOSE_MARKER = '\n```\n'

interface MarkerMatch {
  index: number
  length: number
}

function findPtcStartMarker(content: string): MarkerMatch | null {
  const directIndex = content.indexOf(PTC_CODE_EXECUTION_START_MARKER)
  const newlineMarker = `\n${PTC_CODE_EXECUTION_START_MARKER}`
  const newlineIndex = content.indexOf(newlineMarker)

  if (directIndex === -1 && newlineIndex === -1) return null
  if (newlineIndex !== -1 && (directIndex === -1 || newlineIndex < directIndex)) {
    return { index: newlineIndex, length: newlineMarker.length }
  }
  return { index: directIndex, length: PTC_CODE_EXECUTION_START_MARKER.length }
}

/**
 * Returns true when `content` (the text immediately after a `\n```\n` fence)
 * begins with a PTC result tag produced by the stream-mapper.
 */
function isPtcResultTag(content: string): boolean {
  const normalized = content.startsWith('\n') ? content.slice(1) : content
  return (
    normalized.startsWith('[Output]\n')
    || normalized.startsWith('[Stderr]\n')
    || normalized.startsWith('[Error:')
    || normalized.startsWith('[Encrypted Output]\n')
  )
}

/**
 * Finds an orphaned PTC result block in content that is NOT inside a code
 * suppression region. An orphaned result is a `\n```\n` close-fence followed
 * immediately by a result tag (`[Output]`, `[Stderr]`, `[Error: …]`, or
 * `[Encrypted Output]`).
 *
 * These appear when `code_execution_result` events arrive in a later
 * streaming iteration than their corresponding `code_execution_start`,
 * so the filter is not in suppression mode when they appear.
 */
function findOrphanedPtcResult(content: string): MarkerMatch | null {
  let searchFrom = 0
  while (searchFrom < content.length) {
    const idx = content.indexOf(PTC_CODE_FENCE_CLOSE_MARKER, searchFrom)
    if (idx === -1) return null
    const afterFence = content.slice(idx + PTC_CODE_FENCE_CLOSE_MARKER.length)
    if (isPtcResultTag(afterFence)) {
      return { index: idx, length: PTC_CODE_FENCE_CLOSE_MARKER.length }
    }
    searchFrom = idx + 1
  }
  return null
}

function stripLeadingPtcResultChunk(content: string): string {
  const normalized = content.startsWith('\n') ? content.slice(1) : content
  if (normalized.startsWith('[Error:')) {
    return ''
  }
  if (
    normalized.startsWith('[Output]\n')
    || normalized.startsWith('[Stderr]\n')
    || normalized.startsWith('[Encrypted Output]\n')
  ) {
    return ''
  }
  return content
}

/**
 * Stateful filter that strips PTC (Programmatic Tool Calling) code execution
 * transcript blocks from streamed assistant content. The tool execution is
 * displayed via ToolEventsPanel expansion panels instead.
 *
 * Each instance tracks whether suppression is currently active across chunks.
 * Call `reset()` between streaming sessions.
 */
export class PtcTranscriptFilter {
  private suppressing = false

  /** Filter a single streaming chunk, stripping any PTC code blocks. */
  filterChunk(chunk: string): string {
    if (!chunk) return ''

    let remaining = chunk
    let visible = ''

    while (remaining.length > 0) {
      if (!this.suppressing) {
        const startMarker = findPtcStartMarker(remaining)
        const orphanedResult = findOrphanedPtcResult(remaining)

        const startIdx = startMarker?.index ?? Infinity
        const orphanIdx = orphanedResult?.index ?? Infinity

        if (startIdx === Infinity && orphanIdx === Infinity) {
          visible += remaining
          break
        }

        if (orphanIdx < startIdx) {
          // Orphaned result block arrives outside suppression (cross-iteration PCT)
          visible += remaining.slice(0, orphanedResult!.index)
          remaining = remaining.slice(orphanedResult!.index + orphanedResult!.length)
          remaining = stripLeadingPtcResultChunk(remaining)
          continue
        }

        visible += remaining.slice(0, startMarker!.index)
        remaining = remaining.slice(startMarker!.index + startMarker!.length)
        this.suppressing = true
      }

      if (this.suppressing) {
        const closeIndex = remaining.indexOf(PTC_CODE_FENCE_CLOSE_MARKER)
        if (closeIndex === -1) {
          remaining = ''
          break
        }

        remaining = remaining.slice(closeIndex + PTC_CODE_FENCE_CLOSE_MARKER.length)
        this.suppressing = false
        remaining = stripLeadingPtcResultChunk(remaining)
      }
    }

    return visible
  }

  /** Sanitize a complete content snapshot (stateless — does not affect internal state). */
  sanitizeSnapshot(content: string): { content: string; suppressing: boolean } {
    let remaining = content
    let visible = ''
    let localSuppressing = false

    while (remaining.length > 0) {
      if (!localSuppressing) {
        const startMarker = findPtcStartMarker(remaining)
        const orphanedResult = findOrphanedPtcResult(remaining)

        const startIdx = startMarker?.index ?? Infinity
        const orphanIdx = orphanedResult?.index ?? Infinity

        if (startIdx === Infinity && orphanIdx === Infinity) {
          visible += remaining
          break
        }

        if (orphanIdx < startIdx) {
          visible += remaining.slice(0, orphanedResult!.index)
          remaining = remaining.slice(orphanedResult!.index + orphanedResult!.length)
          remaining = stripLeadingPtcResultChunk(remaining)
          continue
        }

        visible += remaining.slice(0, startMarker!.index)
        remaining = remaining.slice(startMarker!.index + startMarker!.length)
        localSuppressing = true
      }

      if (localSuppressing) {
        const closeIndex = remaining.indexOf(PTC_CODE_FENCE_CLOSE_MARKER)
        if (closeIndex === -1) {
          remaining = ''
          break
        }

        remaining = remaining.slice(closeIndex + PTC_CODE_FENCE_CLOSE_MARKER.length)
        localSuppressing = false
        remaining = stripLeadingPtcResultChunk(remaining)
      }
    }

    return { content: visible, suppressing: localSuppressing }
  }

  /** Sanitize an array of messages, stripping PTC blocks from assistant content. */
  sanitizeMessages(messages: ModelMessage[]): ModelMessage[] {
    return messages.map((message) => {
      if (message.role !== 'assistant' || typeof message.content !== 'string') {
        return message
      }

      const sanitized = this.sanitizeSnapshot(message.content)
      return {
        ...message,
        content: sanitized.content
      }
    })
  }

  /** Reset suppression state between streaming sessions. */
  reset(): void {
    this.suppressing = false
  }

  /** Current suppression state (useful for syncing with snapshot-based reconciliation). */
  get isActive(): boolean {
    return this.suppressing
  }

  set isActive(value: boolean) {
    this.suppressing = value
  }
}
